/**
 * Builds Apollo Footer buttonProps from Redux state and env URLs.
 * Replaces inline links from legacy Footer.js.
 * SAF-19125: version link shown only when version exists in store.
 * SAF-18222: external links opened via openExternalLink (reverse tabnabbing).
 */
import { useMemo, useCallback } from 'react';import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getUserInfo } from 'spark-state/common/user/userReducer';
import { getMappedEnterpriseData } from 'spark-state/common/app/reducer';
import { getVersionNumber } from 'spark-state/enterprise/enterpriseReducer';
import {
    privacyPolicyLinkURL,
    termsOfUseLinkURL,
    versionLink,
} from 'spark-api/safetyLetter/commonSafetyLetterService';
import { buildSupportLink, openExternalLink } from '../supportLinkUtils';

export function useSparkFooterLinks() {
    const location = useLocation();
    const user = useSelector(getUserInfo);
    const mappedEnterprise = useSelector(getMappedEnterpriseData);
    const version = useSelector((state) => getVersionNumber(state) || '');

    const supportLink = useMemo(
        () => buildSupportLink(user, mappedEnterprise, location.pathname),
        [user, mappedEnterprise, location.pathname]
    );

    const openSupport = useCallback(() => {
        openExternalLink(supportLink);
    }, [supportLink]);

    const openVersion = useCallback(() => {
        openExternalLink(versionLink);
    }, []);

    const openTerms = useCallback(() => {
        openExternalLink(termsOfUseLinkURL);
    }, []);

    const openPrivacy = useCallback(() => {
        openExternalLink(privacyPolicyLinkURL);
    }, []);

    return useMemo(() => {
        const buttonProps = [];

        if (version) {
            buttonProps.push({
                label: `Version ${version}`,
                onClick: openVersion,
            });
        }

        buttonProps.push(
            {
                label: 'Support',
                onClick: openSupport,
            },
            {
                label: 'Terms of Use',
                onClick: openTerms,
            },
            {
                label: 'Privacy',
                onClick: openPrivacy,
            }
        );

        return buttonProps;
    }, [version, openVersion, openSupport, openTerms, openPrivacy]);
}
